import _classCallCheck from "/var/lib/jenkins/workspace/GLOTIO_glotio-frontend_master/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/var/lib/jenkins/workspace/GLOTIO_glotio-frontend_master/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createClass";
import _defineProperty from "/var/lib/jenkins/workspace/GLOTIO_glotio-frontend_master/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { logger, extractTraceparentData, baggageHeaderToDynamicSamplingContext, getDomElement } from '@sentry/utils';
import { startIdleTransaction } from '../hubextensions.js';
import { DEFAULT_IDLE_TIMEOUT, DEFAULT_FINAL_TIMEOUT, DEFAULT_HEARTBEAT_INTERVAL } from '../idletransaction.js';
import '@sentry/core';
import { registerBackgroundTabDetection } from './backgroundtab.js';
import { startTrackingWebVitals, startTrackingLongTasks, addPerformanceEntries } from './metrics/index.js';
import { defaultRequestInstrumentationOptions, instrumentOutgoingRequests } from './request.js';
import { instrumentRoutingWithDefaults } from './router.js';
import { WINDOW } from './types.js';
var BROWSER_TRACING_INTEGRATION_ID = 'BrowserTracing';
/** Options for Browser Tracing integration */

var DEFAULT_BROWSER_TRACING_OPTIONS = _objectSpread({
  idleTimeout: DEFAULT_IDLE_TIMEOUT,
  finalTimeout: DEFAULT_FINAL_TIMEOUT,
  heartbeatInterval: DEFAULT_HEARTBEAT_INTERVAL,
  markBackgroundTransactions: true,
  routingInstrumentation: instrumentRoutingWithDefaults,
  startTransactionOnLocationChange: true,
  startTransactionOnPageLoad: true,
  enableLongTask: true,
  _experiments: {}
}, defaultRequestInstrumentationOptions);
/**
 * The Browser Tracing integration automatically instruments browser pageload/navigation
 * actions as transactions, and captures requests, metrics and errors as spans.
 *
 * The integration can be configured with a variety of options, and can be extended to use
 * any routing library. This integration uses {@see IdleTransaction} to create transactions.
 */


var BrowserTracing = /*#__PURE__*/function () {
  function BrowserTracing(_options) {
    _classCallCheck(this, BrowserTracing);

    BrowserTracing.prototype.__init.call(this);

    this.options = _objectSpread(_objectSpread({}, DEFAULT_BROWSER_TRACING_OPTIONS), _options); // Special case: enableLongTask can be set in _experiments
    // TODO (v8): Remove this in v8

    if (this.options._experiments.enableLongTask !== undefined) {
      this.options.enableLongTask = this.options._experiments.enableLongTask;
    } // TODO (v8): remove this block after tracingOrigins is removed
    // Set tracePropagationTargets to tracingOrigins if specified by the user
    // In case both are specified, tracePropagationTargets takes precedence
    // eslint-disable-next-line deprecation/deprecation


    if (_options && !_options.tracePropagationTargets && _options.tracingOrigins) {
      // eslint-disable-next-line deprecation/deprecation
      this.options.tracePropagationTargets = _options.tracingOrigins;
    }

    startTrackingWebVitals();

    if (this.options.enableLongTask) {
      startTrackingLongTasks();
    }
  }
  /**
   * @inheritDoc
   */


  _createClass(BrowserTracing, [{
    key: "__init",
    value: // This class currently doesn't have a static `id` field like the other integration classes, because it prevented
    // @sentry/tracing from being treeshaken. Tree shakers do not like static fields, because they behave like side effects.
    // TODO: Come up with a better plan, than using static fields on integration classes, and use that plan on all
    // integrations.

    /** Browser Tracing integration options */

    /**
     * @inheritDoc
     */
    function __init() {
      this.name = BROWSER_TRACING_INTEGRATION_ID;
    }
  }, {
    key: "setupOnce",
    value: function setupOnce(_, getCurrentHub) {
      var _this = this;

      this._getCurrentHub = getCurrentHub;
      var _this$options = this.options,
          instrumentRouting = _this$options.routingInstrumentation,
          startTransactionOnLocationChange = _this$options.startTransactionOnLocationChange,
          startTransactionOnPageLoad = _this$options.startTransactionOnPageLoad,
          markBackgroundTransactions = _this$options.markBackgroundTransactions,
          traceFetch = _this$options.traceFetch,
          traceXHR = _this$options.traceXHR,
          tracePropagationTargets = _this$options.tracePropagationTargets,
          shouldCreateSpanForRequest = _this$options.shouldCreateSpanForRequest,
          _experiments = _this$options._experiments;
      instrumentRouting(function (context) {
        return _this._createRouteTransaction(context);
      }, startTransactionOnPageLoad, startTransactionOnLocationChange);

      if (markBackgroundTransactions) {
        registerBackgroundTabDetection();
      }

      if (_experiments.enableInteractions) {
        this._registerInteractionListener();
      }

      instrumentOutgoingRequests({
        traceFetch: traceFetch,
        traceXHR: traceXHR,
        tracePropagationTargets: tracePropagationTargets,
        shouldCreateSpanForRequest: shouldCreateSpanForRequest
      });
    }
    /** Create routing idle transaction. */

  }, {
    key: "_createRouteTransaction",
    value: function _createRouteTransaction(context) {
      if (!this._getCurrentHub) {
        (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.warn("[Tracing] Did not create ".concat(context.op, " transaction because _getCurrentHub is invalid."));
        return undefined;
      }

      var _this$options2 = this.options,
          beforeNavigate = _this$options2.beforeNavigate,
          idleTimeout = _this$options2.idleTimeout,
          finalTimeout = _this$options2.finalTimeout,
          heartbeatInterval = _this$options2.heartbeatInterval;
      var isPageloadTransaction = context.op === 'pageload';
      var sentryTraceMetaTagValue = isPageloadTransaction ? getMetaContent('sentry-trace') : null;
      var baggageMetaTagValue = isPageloadTransaction ? getMetaContent('baggage') : null;
      var traceParentData = sentryTraceMetaTagValue ? extractTraceparentData(sentryTraceMetaTagValue) : undefined;
      var dynamicSamplingContext = baggageMetaTagValue ? baggageHeaderToDynamicSamplingContext(baggageMetaTagValue) : undefined;

      var expandedContext = _objectSpread(_objectSpread(_objectSpread({}, context), traceParentData), {}, {
        metadata: _objectSpread(_objectSpread({}, context.metadata), {}, {
          dynamicSamplingContext: traceParentData && !dynamicSamplingContext ? {} : dynamicSamplingContext
        }),
        trimEnd: true
      });

      var modifiedContext = typeof beforeNavigate === 'function' ? beforeNavigate(expandedContext) : expandedContext; // For backwards compatibility reasons, beforeNavigate can return undefined to "drop" the transaction (prevent it
      // from being sent to Sentry).

      var finalContext = modifiedContext === undefined ? _objectSpread(_objectSpread({}, expandedContext), {}, {
        sampled: false
      }) : modifiedContext; // If `beforeNavigate` set a custom name, record that fact

      finalContext.metadata = finalContext.name !== expandedContext.name ? _objectSpread(_objectSpread({}, finalContext.metadata), {}, {
        source: 'custom'
      }) : finalContext.metadata;
      this._latestRouteName = finalContext.name;
      this._latestRouteSource = finalContext.metadata && finalContext.metadata.source;

      if (finalContext.sampled === false) {
        (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.log("[Tracing] Will not send ".concat(finalContext.op, " transaction because of beforeNavigate."));
      }

      (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.log("[Tracing] Starting ".concat(finalContext.op, " transaction on scope"));

      var hub = this._getCurrentHub();

      var location = WINDOW.location;
      var idleTransaction = startIdleTransaction(hub, finalContext, idleTimeout, finalTimeout, true, {
        location: location
      }, // for use in the tracesSampler
      heartbeatInterval);
      idleTransaction.registerBeforeFinishCallback(function (transaction) {
        addPerformanceEntries(transaction);
      });
      return idleTransaction;
    }
    /** Start listener for interaction transactions */

  }, {
    key: "_registerInteractionListener",
    value: function _registerInteractionListener() {
      var _this2 = this;

      var inflightInteractionTransaction;

      var registerInteractionTransaction = function registerInteractionTransaction() {
        var _this2$options = _this2.options,
            idleTimeout = _this2$options.idleTimeout,
            finalTimeout = _this2$options.finalTimeout,
            heartbeatInterval = _this2$options.heartbeatInterval;
        var op = 'ui.action.click';

        if (inflightInteractionTransaction) {
          inflightInteractionTransaction.finish();
          inflightInteractionTransaction = undefined;
        }

        if (!_this2._getCurrentHub) {
          (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.warn("[Tracing] Did not create ".concat(op, " transaction because _getCurrentHub is invalid."));
          return undefined;
        }

        if (!_this2._latestRouteName) {
          (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.warn("[Tracing] Did not create ".concat(op, " transaction because _latestRouteName is missing."));
          return undefined;
        }

        var hub = _this2._getCurrentHub();

        var location = WINDOW.location;
        var context = {
          name: _this2._latestRouteName,
          op: op,
          trimEnd: true,
          metadata: {
            source: _this2._latestRouteSource || 'url'
          }
        };
        inflightInteractionTransaction = startIdleTransaction(hub, context, idleTimeout, finalTimeout, true, {
          location: location
        }, // for use in the tracesSampler
        heartbeatInterval);
      };

      ['click'].forEach(function (type) {
        addEventListener(type, registerInteractionTransaction, {
          once: false,
          capture: true
        });
      });
    }
  }]);

  return BrowserTracing;
}();
/** Returns the value of a meta tag */


function getMetaContent(metaName) {
  // Can't specify generic to `getDomElement` because tracing can be used
  // in a variety of environments, have to disable `no-unsafe-member-access`
  // as a result.
  var metaTag = getDomElement("meta[name=".concat(metaName, "]")); // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access

  return metaTag ? metaTag.getAttribute('content') : null;
}

export { BROWSER_TRACING_INTEGRATION_ID, BrowserTracing, getMetaContent };